import { cn, Tooltip } from '@heroui/react';
import React from 'react';
import { FiPhone, FiMail, FiSmartphone, FiInfo } from 'react-icons/fi';

export type LoginMethod = 'phone' | 'email' | 'emailAndPhone';

export interface LoginMethodOption {
  value: LoginMethod;
  label: string;
  description?: string;
  icon: React.ReactNode;
}

const LOGIN_METHOD_OPTIONS: LoginMethodOption[] = [
  {
    value: 'phone',
    label: 'Phone',
    description: 'Users log in with their phone number',
    icon: <FiPhone className="w-6 h-6" />,
  },
  {
    value: 'email',
    label: 'Email',
    description: 'Users log in with their email address',
    icon: <FiMail className="w-6 h-6" />,
  },
  {
    value: 'emailAndPhone',
    label: 'Email and Phone',
    description: 'Users can log in with either email or phone',
    icon: <FiSmartphone className="w-6 h-6" />,
  },
];

export interface LoginMethodRadioGroupProps {
  value?: LoginMethod | string;
  onChange: (value: LoginMethod) => void;
  isDisabled?: boolean;
  isEditMode?: boolean;
  isRequired?: boolean;
  label?: string;
  description?: string;
  errorMessage?: string;
  'data-testid'?: string;
}

const SelectionCard = ({
  isSelected,
  onClick,
  disabled,
  icon,
  label,
  description,
}: {
  isSelected: boolean;
  onClick: () => void;
  disabled?: boolean;
  icon: React.ReactNode;
  label: string;
  description?: string;
}) => {
  return (
    <div
      className={cn(
        'relative flex items-center gap-4 p-4 rounded-lg border-2 cursor-pointer transition-all',
        'bg-content1 hover:bg-content2',
        isSelected
          ? 'border-primary bg-primary-50/50'
          : 'border-transparent hover:border-default-300',
        disabled && 'opacity-50 cursor-not-allowed pointer-events-none'
      )}
      onClick={disabled ? undefined : onClick}
      role="radio"
      aria-checked={isSelected}
    >
      <div className="flex-1 flex items-center gap-3 min-w-0">
        <div
          className={cn(
            'shrink-0 text-default-500',
            isSelected && 'text-primary'
          )}
        >
          {icon}
        </div>
        <div className="flex flex-col gap-1 min-w-0">
          <span
            className={cn(
              'text-sm font-medium text-foreground truncate',
              isSelected && 'text-primary-700'
            )}
          >
            {label}
          </span>
          {description && (
            <span className="text-xs text-default-500 line-clamp-2">
              {description}
            </span>
          )}
        </div>
      </div>

      {/* Custom Radio Indicator */}
      <div
        className={cn(
          'shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors',
          isSelected
            ? 'border-primary bg-primary'
            : 'border-default-400 bg-transparent'
        )}
      >
        {isSelected && <div className="w-2 h-2 rounded-full bg-white" />}
      </div>
    </div>
  );
};

const ReadOnlyDisplay = ({
  value,
  options,
}: {
  value?: LoginMethod | string;
  options: LoginMethodOption[];
}) => {
  const selectedOption = options.find((opt) => opt.value === value);
  const otherOptions = options.filter((opt) => opt.value !== value);

  const getEmoji = (method: LoginMethod) => {
    switch (method) {
      case 'phone':
        return '📱';
      case 'email':
        return '📧';
      case 'emailAndPhone':
        return '🔐';
      default:
        return '•';
    }
  };

  const tooltipContent = (
    <div className="space-y-2 py-1">
      <div className="text-xs font-semibold mb-2">
        🔄 Other Login Method Options:
      </div>
      {otherOptions.map((option) => (
        <div key={option.value} className="text-xs">
          <span className="font-medium">
            {getEmoji(option.value)} {option.label}:
          </span>{' '}
          {option.description}
        </div>
      ))}
      <div className="text-xs opacity-75 mt-2 pt-2 border-t border-divider">
        ⚠️ Note: Login method cannot be changed after user creation.
      </div>
    </div>
  );

  if (!selectedOption) {
    return (
      <div className="p-4 rounded-lg border-2 border-default-200 bg-content1">
        <span className="text-sm text-default-500">Not set</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3">
      <div
        className={cn(
          'flex items-center gap-3 p-4 rounded-lg border-2 border-default-200 bg-content1 flex-1'
        )}
      >
        <div className="text-primary shrink-0">{selectedOption.icon}</div>
        <div className="flex flex-col gap-1 min-w-0">
          <span className="text-sm font-medium text-foreground">
            {selectedOption.label}
          </span>
          {selectedOption.description && (
            <span className="text-xs text-default-500">
              {selectedOption.description}
            </span>
          )}
        </div>
      </div>
      <Tooltip
        content={tooltipContent}
        placement="top"
        classNames={{
          content: 'p-3 max-w-xs',
        }}
      >
        <button
          type="button"
          className="shrink-0 p-2 text-default-400 hover:text-default-600 transition-colors"
          aria-label="View other login method options"
        >
          <FiInfo className="w-5 h-5" />
        </button>
      </Tooltip>
    </div>
  );
};

export const LoginMethodRadioGroup: React.FC<LoginMethodRadioGroupProps> = ({
  value,
  onChange,
  isDisabled = false,
  isEditMode = false,
  isRequired = false,
  label = 'Login Method',
  description = 'Choose how users will authenticate',
  errorMessage,
  'data-testid': dataTestId,
}) => {
  const handleChange = (selectedValue: string) => {
    if (!isDisabled && !isEditMode) {
      onChange(selectedValue as LoginMethod);
    }
  };

  // In edit mode, show read-only display
  if (isEditMode) {
    return (
      <div className="space-y-3" data-testid={dataTestId}>
        <div className="flex flex-col gap-1">
          <span className="text-sm font-medium text-foreground">
            {label} {isRequired && <span className="text-danger">*</span>}
          </span>
          {description && (
            <span className="text-sm text-default-500">{description}</span>
          )}
        </div>

        <ReadOnlyDisplay value={value} options={LOGIN_METHOD_OPTIONS} />

        {errorMessage && (
          <div className="text-tiny text-danger">{errorMessage}</div>
        )}
      </div>
    );
  }

  // In create mode, show selectable cards
  return (
    <div className="space-y-3" data-testid={dataTestId}>
      <div className="flex flex-col gap-1">
        <span className="text-sm font-medium text-foreground">
          {label} {isRequired && <span className="text-danger">*</span>}
        </span>
        {description && (
          <span className="text-sm text-default-500">{description}</span>
        )}
      </div>

      <div
        className="grid grid-cols-1 gap-4 sm:grid-cols-3"
        role="radiogroup"
        aria-label={label}
      >
        {LOGIN_METHOD_OPTIONS.map((option) => (
          <SelectionCard
            key={option.value}
            isSelected={value === option.value}
            onClick={() => handleChange(option.value)}
            disabled={isDisabled}
            icon={option.icon}
            label={option.label}
            description={option.description}
          />
        ))}
      </div>

      {errorMessage && (
        <div className="text-tiny text-danger">{errorMessage}</div>
      )}
    </div>
  );
};
